import { t } from 'i18next'
import { useCallback, useEffect, useState } from 'react'
import { useCreateBillMutation, PasserByBillsDocument, useUpdateBillMutation, BillCategoryType, useDeleteBillMutation, PasserByBillsQueryVariables } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { BillData } from '../creation/bill-creation-modal'

export default function usePasserbyBillListHook(propertyId: string, passerByBillVariablesState: PasserByBillsQueryVariables, paginationLimit: number, closeDialog: (success?: boolean) => void) {
  const { id: tenantId } = useTenant()
  const showErrorSnackBar = useErrorSnackBar()
  const showSuccessSnackBar = useSuccessSnackBar()
  const [loading, setLoading] = useState(false)

  const [createBillMutation, { loading: creationLoading }] = useCreateBillMutation({
    refetchQueries: () => [{ query: PasserByBillsDocument, variables: passerByBillVariablesState }],
    awaitRefetchQueries: true,
  })

  const [updateBillMutation, { loading: updateLoading }] = useUpdateBillMutation({
    refetchQueries: () => [{ query: PasserByBillsDocument, variables: passerByBillVariablesState }],
    awaitRefetchQueries: true,
  })

  const [deleteBillMutation, { loading: deleteLoading }] = useDeleteBillMutation({
    refetchQueries: () => [{ query: PasserByBillsDocument, variables: passerByBillVariablesState }],
    awaitRefetchQueries: true,
  })

  const editBill = useCallback(
    async (billId: string, passerbyBill: BillData) => {
      try {
        const result = await updateBillMutation({
          variables: {
            updateBillId: billId,
            value: {
              tenantId: tenantId,
              propertyId: propertyId,
              ownerId: passerbyBill.ownerId,
              category: BillCategoryType.EXTRA,
              description: passerbyBill.description,
            },
          },
        })
        setLoading(false)

        const updatedBill = result.data?.updateBill
        if (updatedBill === undefined) {
          showErrorSnackBar(result.errors)
          closeDialog()
        } else {
          showSuccessSnackBar(t('pms.booking.passerby-bill.creation-dialog.update_success'))
          closeDialog(true)
        }
      } catch (e) {
        setLoading(false)
        showErrorSnackBar(e)
      }
    },
    [updateBillMutation, tenantId, propertyId, setLoading, showErrorSnackBar, closeDialog],
  )

  const createNewBill = useCallback(
    async (passerbyBill: BillData) => {
      try {
        const result = await createBillMutation({
          variables: {
            value: {
              tenantId: tenantId,
              propertyId: propertyId,
              ownerId: passerbyBill.ownerId,
              category: BillCategoryType.EXTRA,
              description: passerbyBill.description,
            },
          },
        })
        setLoading(false)

        const newBill = result.data?.createBill
        if (newBill === undefined) {
          showErrorSnackBar(result.errors)
          closeDialog()
        } else {
          showSuccessSnackBar(t('pms.booking.passerby-bill.creation-dialog.create_success'))
          closeDialog(true)
        }
      } catch (e) {
        setLoading(false)
        showErrorSnackBar(e)
      }
    },
    [createBillMutation, tenantId, propertyId, setLoading, showErrorSnackBar, closeDialog],
  )

  const deleteBill = useCallback(
    async (billId: string, refetchQueries?: string[]) => {
      try {
        const result = await deleteBillMutation({
          variables: {
            deleteBillTenantId: tenantId,
            deleteBillId: billId,
          },
          refetchQueries,
        })
        setLoading(false)

        const deletedBill = result.data?.deleteBill
        if (deletedBill === undefined) {
          showErrorSnackBar(result.errors)
        } else {
          showSuccessSnackBar(t('pms.booking.passerby-bill.creation-dialog.delete_success'))
        }
        closeDialog()
      } catch (e) {
        setLoading(false)
        showErrorSnackBar(e)
      }
    },
    [deleteBillMutation, setLoading, showSuccessSnackBar, showErrorSnackBar, closeDialog, tenantId],
  )

  useEffect(() => {
    setLoading(creationLoading || updateLoading || deleteLoading)
  }, [creationLoading, updateLoading, deleteLoading])

  return { loading, editBill, createNewBill, deleteBill }
}
